import {
    Directive,
    ElementRef,
    AfterViewInit,
    OnDestroy,
    HostListener,
    NgZone,
} from "@angular/core";

/**
 * DEV-20345 — ConsoleHeaderOffsetDirective
 *
 * The fixed console header (.consoleHeader) changes height depending on
 * viewport width (its action row wraps to a second line in split-screen /
 * reduced widths). Any element pinned below it — the sticky paginator, the
 * job/glitch detail panel — needs an offset that tracks the header's ACTUAL
 * bottom edge, not a hardcoded px value (which is only correct at one width).
 *
 * This directive measures the header's rendered bottom edge and publishes it
 * as a global CSS custom property `--console-header-bottom` on <html>, which
 * the relevant CSS reads via `top: var(--console-header-bottom, 110px)`.
 * It re-measures on resize and after view init.
 *
 * Apply with: <div class="consoleHeader" consoleHeaderOffset> ... </div>
 */
@Directive({
    selector: "[consoleHeaderOffset]",
})
export class ConsoleHeaderOffsetDirective implements AfterViewInit, OnDestroy {
    private rafId: number | null = null;

    constructor(private el: ElementRef<HTMLElement>, private zone: NgZone) {}

    ngAfterViewInit(): void {
        // measure after the view settles (header content/wrapping finalised)
        this.scheduleMeasure();
        setTimeout(() => this.scheduleMeasure(), 150);
    }

    @HostListener("window:resize")
    onResize(): void {
        this.scheduleMeasure();
    }

    private scheduleMeasure(): void {
        // run outside Angular to avoid change-detection churn
        this.zone.runOutsideAngular(() => {
            if (this.rafId != null) {
                cancelAnimationFrame(this.rafId);
            }
            this.rafId = requestAnimationFrame(() => {
                this.measure();
                this.rafId = null;
            });
        });
    }

    private measure(): void {
        const el = this.el.nativeElement;
        if (!el) {
            return;
        }
        const rect = el.getBoundingClientRect();
        // bottom edge of the fixed header relative to the viewport top
        const bottom = Math.round(rect.bottom);
        if (bottom > 0) {
            document.documentElement.style.setProperty(
                "--console-header-bottom",
                bottom + "px"
            );
        }
    }

    ngOnDestroy(): void {
        if (this.rafId != null) {
            cancelAnimationFrame(this.rafId);
        }
    }
}
